import { folderOf, scanFbxTextures } from './render-fbx-textures';
import { fileExtension, formatBytes, isJunkPath, isModelFile, isTextureFile, RenderUploadFile, RenderUploadProblem } from './render-upload.inspector';
import { canInflate, extractZipEntry, isSupportedMethod, readZipDirectory, safeZipPath, ZipDirectory, ZipEntry, ZipError } from './render-zip';
import { RENDER_UPLOAD_LIMIT_BYTES } from './render.data';

// Between the drop and the inspection: ZIP archives are opened in the browser and become folders
// ('chair.zip' → 'chair/…'), and a drop with several models gets one — LOD0 of a LOD set, the FBX
// of a model that also came as USDZ — or asks the user to choose. Only then is anything unpacked,
// and only the chosen model, the textures, and whatever else the FBX names (a .dds). The archive
// itself is never uploaded. Analysis and the 34 cases: docs/render-landing-port.md, решение 26.

export interface RenderModelOption {
  path: string;
  size: number;
}

export interface RenderPrepareProgress {
  // The archive being opened or unpacked.
  name: string;
  // 'open': its table of contents is read; 'unpack': the files are taken out.
  phase: 'open' | 'unpack';
  percent: number;
  doneBytes: number;
  totalBytes: number;
}

export type RenderPreparation =
  // The files to inspect: one model at most, other models left out. `archive`: the one they came
  // out of, if any.
  | { kind: 'ready'; files: RenderUploadFile[]; note?: string; archive?: string }
  // `source`: the archive or folder all the models sit in, when they share one.
  | { kind: 'choose'; models: RenderModelOption[]; source?: string }
  // `report` is for the error log: numbers and codes, never file names.
  | { kind: 'problem'; problem: RenderUploadProblem; report?: Record<string, string | number> };

export interface RenderPrepareOptions {
  // The model the user chose from a 'choose' result, by path.
  chosenPath?: string;
  // The files join a model that waits for its textures: no model needed, textures taken.
  texturesOnly?: boolean;
  // How much an archive may unpack to on this device (renderArchiveLimitBytes).
  limitBytes: number;
  onProgress?: (progress: RenderPrepareProgress) => void;
  shouldStop?: () => boolean;
}

// Past this an archive's table of contents is not even read.
const ARCHIVE_MAX_ENTRIES = 10000;
// Storage object names stop at 1024 bytes and 'uploaded_models/{uid}/{stamp}/' takes about 80.
const MAX_PATH_BYTES = 900;
// A phone keeps unpacked files in memory (Safari does not page Blobs to disk).
const PHONE_ARCHIVE_LIMIT_BYTES = 200 * 1024 * 1024;

const OTHER_ARCHIVES: Record<string, string> = { '.rar': 'RAR', '.7z': '7z', '.tar': 'TAR', '.tgz': 'TAR', '.gz': 'GZip', '.bz2': 'BZip2', '.xz': 'XZ' };
const PARTIAL_DOWNLOAD = /\.(crdownload|part|partial|download)$/i;
const SPLIT_PART = /\.(z\d{2}|zip\.\d{3})$/i;
const FOREIGN_MODELS: Record<string, string> = {
  '.glb': 'glTF', '.gltf': 'glTF', '.obj': 'OBJ', '.blend': 'Blender', '.max': '3ds Max', '.c4d': 'Cinema 4D',
  '.ma': 'Maya', '.mb': 'Maya', '.3ds': '3DS', '.dae': 'COLLADA', '.skp': 'SketchUp', '.stl': 'STL', '.ply': 'PLY',
  '.usda': 'USDA', '.usdc': 'USDC',
};
// FBX first: it is the author's own file, and its weak spot, the textures, is checked here.
const FORMAT_RANK: Record<string, number> = { '.fbx': 0, '.usdz': 1, '.usd': 2 };

function isPhoneBrowser(): boolean {
  const agent = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const iPadAsMac = typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod|Android/i.test(agent) || iPadAsMac;
}

export function renderArchiveLimitBytes(): number {
  return isPhoneBrowser() ? PHONE_ARCHIVE_LIMIT_BYTES : RENDER_UPLOAD_LIMIT_BYTES;
}

// A file input's `accept`. A phone's picker goes by the system's file types, and FBX is not one: with
// a list the file is greyed out and can't be chosen (designer, 2026-11-04). There it offers every
// file, and the checks after the pick refuse what isn't ours, as for a drop.
export function renderPickerAccept(extensions: string[]): string {
  return isPhoneBrowser() ? '' : extensions.join(',');
}

export function isArchiveFile(name: string): boolean {
  return fileExtension(name) === '.zip';
}

// A file of the drop, or an entry of an archive in it.
interface Candidate {
  path: string;
  size: number;
  loose?: RenderUploadFile;
  zip?: { archive: string; blob: Blob; directory: ZipDirectory; entry: ZipEntry };
}

export async function prepareFiles(files: RenderUploadFile[], options: RenderPrepareOptions): Promise<RenderPreparation> {
  let archiveName = '';
  // Advice mentions ZIPs only where the browser can unpack one.
  const zipAdvice = canInflate();
  try {
    // 1. What can be told by the name alone.
    for (const item of files) {
      const name = item.file.name;
      const kind = OTHER_ARCHIVES[fileExtension(name)];
      if (PARTIAL_DOWNLOAD.test(name)) {
        return problem(`${name} hasn’t finished downloading.`, 'Wait for the download to finish, then drop it again.', { failure: 'partial' }, 'file');
      }
      if (SPLIT_PART.test(name)) {
        return problem(`${name} is one part of a split archive.`, zipAdvice ? 'Join the parts into one ZIP, or unpack them and drop the folder.' : 'Unpack the parts and drop the folder.', { failure: 'split' });
      }
      if (kind) {
        return problem(`${name} is a ${kind} archive.`, zipAdvice ? 'Only ZIP archives open here. Unpack it and drop the folder.' : 'Unpack it and drop the folder.', { failure: 'other-format', format: kind });
      }
    }

    // 2. Every archive's table of contents, and an archive inside one that has no model (one level:
    //    Sketchfab keeps the original in source/*.zip). Nothing is unpacked yet but those inner ones.
    const candidates: Candidate[] = [];
    let archives = 0;
    for (const item of files) {
      if (!isArchiveFile(item.file.name)) {
        candidates.push({ path: item.path, size: item.file.size, loose: item });
        continue;
      }
      archives++;
      archiveName = item.file.name;
      options.onProgress?.({ name: item.file.name, phase: 'open', percent: 0, doneBytes: 0, totalBytes: item.file.size });
      const folder = joinPath(folderOf(item.path), safeZipPath(item.file.name.replace(/\.zip$/i, '')) || 'archive');
      const own = await listArchive(item.file.name, item.file, folder);
      if (own.length === 0) {
        return problem(`${item.file.name} is empty.`, zipAdvice ? 'Choose an FBX or USDZ model, or a ZIP with one.' : 'Choose an FBX or USDZ model.', { failure: 'empty' }, 'file');
      }
      candidates.push(...own.filter((candidate) => !isArchiveFile(candidate.path)));
      if (!own.some((candidate) => isModelFile(candidate.path))) {
        for (const inner of own.filter((candidate) => isArchiveFile(candidate.path))) {
          const blob = await extract(inner, options);
          const innerFolder = joinPath(folderOf(inner.path), inner.path.split('/').pop()?.replace(/\.zip$/i, '') || 'archive');
          candidates.push(...(await listArchive(item.file.name, blob, innerFolder)).filter((candidate) => !isArchiveFile(candidate.path)));
        }
      }
    }

    // 3. The model. Nothing to choose from: say what there is instead.
    const models = candidates.filter((candidate) => isModelFile(candidate.path));
    if (models.length === 0 && !options.texturesOnly) {
      if (archives === 0) {
        return { kind: 'ready', files: files.slice() };
      }
      const foreign = candidates.find((candidate) => FOREIGN_MODELS[fileExtension(candidate.path)]);
      return foreign
        ? problem(`This archive has a ${FOREIGN_MODELS[fileExtension(foreign.path)]} model (${baseName(foreign.path)}).`, 'Export it as FBX or USDZ, then drop it here.', { failure: 'foreign-model', format: fileExtension(foreign.path) }, 'file')
        : problem(`No model in ${archiveName}.`, zipAdvice ? 'Choose an FBX or USDZ model, or a ZIP with one.' : 'Choose an FBX or USDZ model.', { failure: 'no-model' }, 'file');
    }
    let model: Candidate | undefined;
    let note: string | undefined;
    const chosen = options.chosenPath ? models.find((candidate) => candidate.path === options.chosenPath) : undefined;
    if (chosen) {
      model = chosen;
    } else if (models.length > 0) {
      const pick = pickModel(models);
      if (!pick) {
        return { kind: 'choose', models: models.map((candidate) => ({ path: candidate.path, size: candidate.size })).sort((a, b) => a.path.localeCompare(b.path)), source: sourceOf(models) };
      }
      ({ model, note } = pick);
    }

    // 4. What goes with it: every texture and every other file of the drop, never another model or
    //    an archive's non-texture leftovers; then what the FBX names that is not a texture by
    //    extension (a .dds), once the model is out and has been read.
    const wanted = candidates.filter((candidate) => candidate === model || (!isModelFile(candidate.path) && (candidate.loose || isTextureFile(candidate.path))));
    const packed = wanted.filter((candidate) => candidate.zip);
    guardArchiveEntries(packed, options.limitBytes);

    const total = packed.reduce((sum, candidate) => sum + candidate.size, 0);
    let done = 0;
    let shown = -1;
    const progress = (candidate: Candidate) => (bytes: number) => {
      done += bytes;
      const percent = total ? Math.min(100, Math.floor((done / total) * 100)) : 100;
      if (percent !== shown) {
        shown = percent;
        options.onProgress?.({ name: candidate.zip?.archive || '', phase: 'unpack', percent, doneBytes: done, totalBytes: total });
      }
    };

    const out = new Map<Candidate, RenderUploadFile>();
    const take = async (candidate: Candidate) => {
      if (candidate.loose) {
        out.set(candidate, candidate.loose);
        return;
      }
      archiveName = candidate.zip?.archive || archiveName;
      const blob = await extract(candidate, options, progress(candidate));
      out.set(candidate, { path: candidate.path, file: new File([blob], baseName(candidate.path)) });
    };

    if (model) {
      await take(model);
    }
    if (model && fileExtension(model.path) === '.fbx') {
      const scan = await scanFbxTextures(out.get(model)?.file as File);
      const named = new Set<string>();
      (scan?.textures || []).forEach((texture) => texture.names.forEach((name) => named.add(name.normalize('NFC').toLowerCase())));
      const extras = candidates.filter((candidate) => candidate.zip && !wanted.includes(candidate) && !isModelFile(candidate.path) && named.has(baseName(candidate.path).toLowerCase()));
      if (extras.length) {
        guardArchiveEntries([...packed, ...extras], options.limitBytes);
        wanted.push(...extras);
      }
    }
    for (const candidate of wanted) {
      if (!out.has(candidate)) {
        await take(candidate);
      }
    }
    const archive = model?.zip?.archive || (packed.length ? packed[0].zip?.archive : undefined);
    return { kind: 'ready', files: wanted.map((candidate) => out.get(candidate) as RenderUploadFile), note, archive };
  } catch (error) {
    return failed(error, archiveName, options.limitBytes);
  }
}

// One model out of several, when the choice is obvious: the same model in versions of detail
// (Quixel's _LOD0…_LOD5) or in formats (billboard.fbx + Billboard.usdz). null: ask the user.
export function pickModel<T extends { path: string }>(models: T[]): { model: T; note?: string } | null {
  if (models.length === 1) {
    return { model: models[0] };
  }
  const key = (path: string) => stem(path).replace(/[\s._-]*lod\d+$/i, '');
  if (new Set(models.map((candidate) => key(candidate.path))).size > 1) {
    return null;
  }
  const lod = (path: string) => Number((/lod(\d+)$/i.exec(stem(path)) || [])[1] || 0);
  const rank = (path: string) => FORMAT_RANK[fileExtension(path)] ?? 9;
  const depth = (path: string) => path.split('/').length;
  const sorted = models.slice().sort((a, b) => lod(a.path) - lod(b.path) || rank(a.path) - rank(b.path) || depth(a.path) - depth(b.path) || a.path.localeCompare(b.path));
  const model = sorted[0];
  const name = baseName(model.path);
  const lods = new Set(models.map((candidate) => lod(candidate.path)));
  const formats = new Set(models.map((candidate) => fileExtension(candidate.path)));
  if (lods.size > 1) {
    return { model, note: `Using ${name}, the most detailed of ${lods.size} versions.` };
  }
  if (formats.size > 1) {
    return { model, note: `Using ${name}. The same model in ${Array.from(formats).filter((format) => format !== fileExtension(name)).map((format) => format.slice(1).toUpperCase()).join(' and ')} is left out.` };
  }
  return { model, note: `Using ${model.path}, one of ${models.length} copies.` };
}

// --- archives ---

async function listArchive(archive: string, blob: Blob, folder: string): Promise<Candidate[]> {
  const directory = await readZipDirectory(blob, ARCHIVE_MAX_ENTRIES);
  const seen = new Set<string>();
  const list: Candidate[] = [];
  for (const entry of directory.entries) {
    const inside = entry.directory || entry.symlink ? null : safeZipPath(entry.name);
    if (!inside || isJunkPath(inside)) {
      continue;
    }
    const path = joinPath(folder, inside);
    if (seen.has(path.toLowerCase())) {
      continue;
    }
    seen.add(path.toLowerCase());
    list.push({ path, size: entry.size, zip: { archive, blob, directory, entry } });
  }
  return list;
}

// Before a byte is unpacked: passwords, compression the browser lacks, the device's limit, paths
// too long for Storage. Only the entries we would take count.
function guardArchiveEntries(packed: Candidate[], limitBytes: number): void {
  for (const candidate of packed) {
    const entry = (candidate.zip as NonNullable<Candidate['zip']>).entry;
    if (entry.encrypted) {
      throw new ZipError('encrypted', candidate.zip?.archive);
    }
    if (!isSupportedMethod(entry)) {
      throw new ZipError('compression', candidate.zip?.archive);
    }
    if (entry.method !== 0 && !canInflate()) {
      throw new ZipError('browser', candidate.zip?.archive);
    }
    if (new TextEncoder().encode(candidate.path).length > MAX_PATH_BYTES) {
      throw new PathTooLong(candidate.zip?.archive || '');
    }
  }
  const unpacked = packed.reduce((sum, candidate) => sum + candidate.size, 0);
  if (unpacked > limitBytes) {
    throw new TooLarge(unpacked);
  }
}

async function extract(candidate: Candidate, options: RenderPrepareOptions, onBytes?: (bytes: number) => void): Promise<Blob> {
  const zip = candidate.zip as NonNullable<Candidate['zip']>;
  try {
    return await extractZipEntry(zip.blob, zip.directory, zip.entry, onBytes, options.shouldStop);
  } catch (error) {
    // Name the damaged file, not the archive: that is what the user can check.
    if (error instanceof ZipError && error.failure === 'corrupt-file') {
      throw new ZipError('corrupt-file', `${baseName(candidate.path)} in ${zip.archive}`);
    }
    throw error;
  }
}

class PathTooLong extends Error {
  public readonly archive: string;

  constructor(archive: string) {
    super('path too long');
    this.archive = archive;
  }
}

class TooLarge extends Error {
  public readonly bytes: number;

  constructor(bytes: number) {
    super('too large');
    this.bytes = bytes;
  }
}

function failed(error: unknown, archive: string, limitBytes: number): RenderPreparation {
  const name = archive || 'This archive';
  if (error instanceof TooLarge) {
    const phone = limitBytes < RENDER_UPLOAD_LIMIT_BYTES;
    return problem(
      `These files exceed the ${formatBytes(limitBytes, 0)} limit${phone ? ' on this device' : ''}.`,
      `${formatBytes(error.bytes)} once unpacked. ${phone ? 'Use a computer, or choose a smaller model.' : 'Reduce the texture sizes or choose a smaller model.'}`,
      { failure: 'too-large', bytes: error.bytes },
      'file',
    );
  }
  if (error instanceof PathTooLong) {
    return problem(`A file path in ${error.archive || name} is too long.`, 'Unpack it and drop the folder.', { failure: 'path-too-long' });
  }
  if (error instanceof RangeError) {
    return problem(`There isn’t enough memory to open ${name} on this device.`, 'Unpack it and drop the folder, or use a computer.', { failure: 'memory' });
  }
  if (!(error instanceof ZipError)) {
    return problem(`${name} couldn’t be opened.`, 'Unpack it and drop the folder.', { failure: 'unknown', error: String((error as Error)?.name || error) });
  }
  const report = { failure: error.failure };
  const zipAdvice = canInflate();
  // The guard names the archive an entry belongs to; with several archives that is not `name`.
  const which = error.entry || name;
  switch (error.failure) {
    case 'split':
      return problem(`${name} is one part of a split archive.`, zipAdvice ? 'Join the parts into one ZIP, or unpack them and drop the folder.' : 'Unpack the parts and drop the folder.', report);
    case 'too-many':
      return problem(`${name} has ${Number(error.entry).toLocaleString('en-US')} files.`, 'That is more than this page can open. Drop the model’s folder instead.', { ...report, entries: Number(error.entry) });
    case 'encrypted':
      return problem(`${which} is password-protected.`, 'Unpack it and drop the folder.', report);
    case 'compression':
      return problem(`${which} uses compression your browser can’t open.`, zipAdvice ? 'Unpack it and drop the folder, or save it again as a standard ZIP.' : 'Unpack it and drop the folder.', report);
    case 'browser':
      return problem('Your browser can’t open ZIP archives.', 'Unpack it and drop the folder, or update your browser.', report);
    case 'memory':
      return problem(`There isn’t enough memory to open ${name} on this device.`, 'Unpack it and drop the folder, or use a computer.', report);
    case 'corrupt-file':
      return problem(`${error.entry} is damaged.`, 'Download the archive again.', report, 'file');
    case 'aborted':
      return { kind: 'problem', problem: { code: 'archive', title: '', text: '' }, report };
    default:
      // damaged, too-big (a stream longer than promised)
      return problem(`${name} is damaged or incomplete.`, 'Download it again, or unpack it and drop the folder.', report);
  }
}

// `fix`: what the refusal's button offers — most archive problems are solved by unpacking it and
// dropping the folder.
function problem(title: string, text: string, report?: Record<string, string | number>, fix: 'file' | 'folder' = 'folder'): RenderPreparation {
  return { kind: 'problem', problem: { code: 'archive', title, text, fix }, report };
}

// The archive, or else the top folder, that every model of a choice sits in.
function sourceOf(models: Candidate[]): string | undefined {
  const archives = new Set(models.map((candidate) => candidate.zip?.archive || ''));
  if (archives.size === 1 && !archives.has('')) {
    return Array.from(archives)[0];
  }
  const tops = new Set(models.map((candidate) => (candidate.path.includes('/') ? candidate.path.split('/')[0] : '')));
  return tops.size === 1 && !tops.has('') ? Array.from(tops)[0] : undefined;
}

function joinPath(folder: string, path: string): string {
  return folder ? `${folder}/${path}` : path;
}

function baseName(path: string): string {
  return path.split('/').pop() || path;
}

function stem(path: string): string {
  const name = baseName(path).normalize('NFC').toLowerCase();
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}
